import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, ArrowLeftRight, Save, Dumbbell } from 'lucide-react';
import { EXERCISES, Exercise } from '@/data/exercises';
import { WorkoutProgramExercise } from '@/hooks/useWorkoutPrograms';
import { useUnitFormatters } from '@/hooks/useUnits';
import { displayWeightToKg, kgToDisplayWeight } from '@/lib/units';

interface EditProgramExerciseModalProps {
  exercise: WorkoutProgramExercise | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (exerciseId: string, updates: Partial<WorkoutProgramExercise>) => Promise<void>;
  onReplace: (exerciseId: string, newExercise: Exercise, keepSetsReps: boolean) => Promise<void>;
}

export const EditProgramExerciseModal = ({
  exercise,
  isOpen,
  onClose,
  onSave,
  onReplace,
}: EditProgramExerciseModalProps) => {
  const { t } = useTranslation();
  const { units, weightLabel } = useUnitFormatters();
  const [tab, setTab] = useState<string>('edit');
  const [sets, setSets] = useState('');
  const [repsMin, setRepsMin] = useState('');
  const [repsMax, setRepsMax] = useState('');
  const [weightDisplay, setWeightDisplay] = useState('');
  const [restSeconds, setRestSeconds] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Initialize form when exercise changes
  useState(() => {
    if (exercise) {
      setSets(exercise.sets?.toString() || '');
      setRepsMin(exercise.reps_min?.toString() || '');
      setRepsMax(exercise.reps_max?.toString() || '');
      setWeightDisplay(
        exercise.weight_kg != null
          ? String(Math.round(kgToDisplayWeight(exercise.weight_kg, units) * 10) / 10)
          : ''
      );
      setRestSeconds(exercise.rest_seconds?.toString() || '');
      setNotes(exercise.notes || '');
    }
  });

  // Reset form when modal opens with new exercise
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
      setTab('edit');
      setSearchQuery('');
    }
  };

  // Sync form state when exercise prop changes
  if (exercise && sets === '' && exercise.sets !== null) {
    setSets(exercise.sets?.toString() || '');
    setRepsMin(exercise.reps_min?.toString() || '');
    setRepsMax(exercise.reps_max?.toString() || '');
    setWeightDisplay(
      exercise.weight_kg != null
        ? String(Math.round(kgToDisplayWeight(exercise.weight_kg, units) * 10) / 10)
        : ''
    );
    setRestSeconds(exercise.rest_seconds?.toString() || '');
    setNotes(exercise.notes || '');
  }

  const filteredExercises = useMemo(() => {
    if (!searchQuery.trim()) return EXERCISES.slice(0, 20);
    const q = searchQuery.toLowerCase();
    return EXERCISES.filter(
      (ex) =>
        ex.name.toLowerCase().includes(q) ||
        ex.category.toLowerCase().includes(q) ||
        ex.muscle_groups.some((mg) => mg.toLowerCase().includes(q))
    ).slice(0, 30);
  }, [searchQuery]);

  const handleSave = async () => {
    if (!exercise) return;
    setSaving(true);
    try {
      await onSave(exercise.id, {
        sets: sets ? parseInt(sets) : null,
        reps_min: repsMin ? parseInt(repsMin) : null,
        reps_max: repsMax ? parseInt(repsMax) : null,
        weight_kg: weightDisplay ? displayWeightToKg(parseFloat(weightDisplay), units) : null,
        rest_seconds: restSeconds ? parseInt(restSeconds) : null,
        notes: notes || null,
      });
      onClose();
    } catch {
      // error handled upstream
    } finally {
      setSaving(false);
    }
  };

  const handleReplace = async (newEx: Exercise) => {
    if (!exercise) return;
    setSaving(true);
    try {
      await onReplace(exercise.id, newEx, true);
      onClose();
    } catch {
      // error handled upstream
    } finally {
      setSaving(false);
    }
  };

  if (!exercise) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="w-full max-w-[420px] sm:max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-lg">{t('components.editProgramExercise.title')}</DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 min-h-0 flex flex-col">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="edit" className="text-xs sm:text-sm">
              <Save className="h-3 w-3 mr-1" /> {t('components.editProgramExercise.editDetails')}
            </TabsTrigger>
            <TabsTrigger value="replace" className="text-xs sm:text-sm">
              <ArrowLeftRight className="h-3 w-3 mr-1" /> {t('components.editProgramExercise.replace')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="edit" className="flex-1 overflow-y-auto space-y-4 mt-4">
            <div>
              <Label className="text-sm font-medium text-muted-foreground">{t('components.editProgramExercise.exerciseLabel')}</Label>
              <p className="font-semibold">{exercise.exercise_name}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="sets">{t('components.editProgramExercise.sets')}</Label>
                <Input
                  id="sets"
                  type="number"
                  value={sets}
                  onChange={(e) => setSets(e.target.value)}
                  placeholder="3"
                />
              </div>
              <div>
                <Label htmlFor="weight">Weight ({weightLabel})</Label>
                <Input
                  id="weight"
                  type="number"
                  step="0.5"
                  value={weightDisplay}
                  onChange={(e) => setWeightDisplay(e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="reps-min">{t('components.editProgramExercise.repsMin')}</Label>
                <Input
                  id="reps-min"
                  type="number"
                  value={repsMin}
                  onChange={(e) => setRepsMin(e.target.value)}
                  placeholder="8"
                />
              </div>
              <div>
                <Label htmlFor="reps-max">{t('components.editProgramExercise.repsMax')}</Label>
                <Input
                  id="reps-max"
                  type="number"
                  value={repsMax}
                  onChange={(e) => setRepsMax(e.target.value)}
                  placeholder="12"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="rest">{t('components.editProgramExercise.restSeconds')}</Label>
              <Input
                id="rest"
                type="number"
                value={restSeconds}
                onChange={(e) => setRestSeconds(e.target.value)}
                placeholder="60"
              />
            </div>

            <div>
              <Label htmlFor="notes">{t('components.editProgramExercise.notes')}</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('components.editProgramExercise.notesPlaceholder')}
                rows={2}
              />
            </div>

            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-gradient-primary text-black"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? t('components.editProgramExercise.saving') : t('components.editProgramExercise.saveChanges')}
            </Button>
          </TabsContent>

          <TabsContent value="replace" className="flex-1 min-h-0 flex flex-col mt-4">
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('components.editProgramExercise.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <p className="text-xs text-muted-foreground mb-2">
              {t('components.editProgramExercise.replacingPrefix')} <span className="font-medium text-foreground">{exercise.exercise_name}</span>
            </p>
            <ScrollArea className="flex-1 min-h-0 max-h-[40vh]">
              <div className="space-y-1 pr-2">
                {filteredExercises.map((ex) => (
                  <button
                    key={ex.id}
                    onClick={() => handleReplace(ex)}
                    disabled={saving}
                    className="w-full text-left p-3 rounded-lg hover:bg-accent/50 transition-colors flex items-center justify-between group"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{ex.name}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {ex.muscle_groups.slice(0, 3).map((mg) => (
                          <Badge key={mg} variant="outline" className="text-[10px] px-1.5 py-0">
                            {mg}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <ArrowLeftRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2" />
                  </button>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
